import { HttpClient, httpResource } from '@angular/common/http';
import { Injectable, computed, inject } from '@angular/core';

import { map } from 'rxjs/operators';

import { Observable } from 'rxjs';
import * as z from 'zod';

import { Product } from '../schemas';
import { storedResource } from '../shared';
import { Lambda } from './lambda';
import { Messenger } from './messenger';

const EmptyObject = z.strictObject({});
const Response = z.object({
  Count: z.number(),
  ScannedCount: z.number(),
  Items: z.array(Product),
});

@Injectable({ providedIn: 'root' })
export class ProductService {
  private http = inject(HttpClient);
  private url = inject(Lambda).getProductsUrl();

  private readonly CACHE_KEY = `lcm:service-cache:product`;

  private productsResource = httpResource<z.infer<typeof Response>>(() => this.url, {
    defaultValue: JSON.parse(window.localStorage.getItem(this.CACHE_KEY) || '{"Items": []}'),
    parse: Response.parse,
  });

  constructor() {
    storedResource(this.productsResource, this.CACHE_KEY, inject(Messenger));
  }

  products = computed(() => this.productsResource.value().Items);
  pending = this.productsResource.isLoading;
  jobNumbers = computed(() =>
    this.products()
      .map((it) => it.JobNumber)
      .sort(),
  );

  reload() {
    this.productsResource.reload();
  }

  addHours(id: string, data: object, type: string) {
    const url = this.url + '/' + id + '/hours/' + type;
    return this.http.put(url, data).pipe(map((data) => EmptyObject.parse(data)));
  }

  updateHours(id: string, data: object, type: string) {
    const url = this.url + '/' + id + '/update-hours/' + type;
    return this.http.put(url, data).pipe(map((data) => EmptyObject.parse(data)));
  }

  removeHours(id: string, type: string, uuid: string | number) {
    const url = this.url + '/' + id + '/hours/' + type + '/' + uuid;
    return this.http.delete(url).pipe(map((data) => EmptyObject.parse(data)));
  }

  addMilestone(id: string, data: object) {
    return this.http.post(this.url + '/' + id + '/milestone', data).pipe(map((data) => Product.parse(data)));
  }

  updateMilestone(JobNumber: string, data: object) {
    return this.http
      .put(this.url + '/' + JobNumber + '/milestone' + '/' + data['Id'], data)
      .pipe(map((data) => Product.parse(data)));
  }

  deleteMilestone(JobNumber: string, Id: string) {
    return this.http
      .delete(this.url + '/' + JobNumber + '/milestone' + '/' + Id)
      .pipe(map((data) => Product.parse(data)));
  }

  save(instance: Product, forEdit = false): Observable<Product> {
    instance = { ...instance };
    delete (instance as Partial<Product>).PurchasedHours;
    delete (instance as Partial<Product>).ProfitableHours;
    delete (instance as Partial<Product>).UnchargeableHours;

    let request: Observable<Product>;
    if (forEdit) {
      const url = this.url + '/' + instance.JobNumber;
      request = this.http.put<Product>(url, instance);
    } else {
      request = this.http.post<Product>(this.url, instance);
    }

    return request.pipe(map((data) => Product.parse(data)));
  }
}
